import { offlineSceneDirName, type OfflineSceneKey } from "@novorender/api";
import { useMemo } from "react";

import { useAppSelector } from "app/redux-store-interactions";
import { useSceneId } from "hooks/useSceneId";
import { selectBackendProvider, selectProcoreContext, selectProjectV2Info } from "slices/explorer";

/**
 * What identifies the open scene's offline files. A version and a slice download different
 * geometry, so both are in the key on the Procore backend, the only one with those ids; a
 * Novorender scene id already names one set of files, and rekeying it would orphan what is in
 * storage. The SDK names the directory, as its read path derives the same name from an asset url.
 */
export function useOfflineSceneKey(): OfflineSceneKey {
    const sceneId = useSceneId();
    const procoreContext = useAppSelector(selectProcoreContext);
    const isProcoreBackendProvider = useAppSelector(selectBackendProvider) === "procore";
    // No version yet means init has not published the resolved one back to the store; the
    // scene id alone is then the whole identity, and every offline action happens after
    // init, so this is a guard rather than a case. The slice goes with it: a slice without
    // a version is not a key the SDK accepts, since it cannot be told from a version.
    const sceneVersionId = isProcoreBackendProvider ? procoreContext?.sceneVersionId : undefined;
    const sceneSliceId = sceneVersionId ? procoreContext?.sceneSliceId : undefined;

    // Kept stable across renders: this is a dependency of the effects that run the
    // offline actions, and a fresh object would re-run whatever they were in the middle of.
    return useMemo(() => ({ sceneId, sceneVersionId, sceneSliceId }), [sceneId, sceneVersionId, sceneSliceId]);
}

/**
 * The name the open scene's offline record goes under: its OPFS directory and its entry in
 * the offline store share it.
 */
export function useOfflineSceneDirName(): string {
    return offlineSceneDirName(useOfflineSceneKey());
}

/**
 * Whether the open scene is its project's own scene rather than a viewer scene, or `undefined`
 * until init has resolved the project. Offline is managed on the project scene only: a viewer
 * scene's copy under its own id would pass the project's data off as its own. The Procore
 * backend has no viewer scenes.
 */
export function useIsProjectScene(): boolean | undefined {
    const sceneId = useSceneId();
    const projectId = useAppSelector(selectProjectV2Info)?.id;
    return projectId === undefined ? undefined : projectId === sceneId;
}
